import { useEffect, useState, Fragment } from 'react'
import cx from 'classnames'
import { useStores } from 'hooks/useStores'
import truncate from 'lodash/truncate'
import { observer } from 'mobx-react'
import { Link } from 'react-router-dom'
import { Dropdown, DropdownMenu, DropdownToggle } from 'reactstrap'
import Avatar from 'components/Avatar'
import EditAccountModal from 'components/EditAccountModal'
import {
  GOOGLE_LINK_OPEN_ACCOUNT_SESSION_KEY,
  OPEN_MY_ACCOUNT_PASSWORD_SETUP_SESSION_KEY,
  OPEN_MY_ACCOUNT_URL_QUERY
} from 'constants/google-sso'
import { ProfileMenuLabels } from 'components/Topbar/constants'
import { S3FileTypeEnum } from 'constants/aws'
import routes from 'routes'
import { getRoleNameFromId } from 'utils/auth/role'
import { getS3FileUrl } from 'utils/images'
import { getName, getRole } from './utils'
import styles from './styles.module.scss'

interface IProfileMenuItem {
  label: ProfileMenuLabels
  icon: string

  redirectTo?: string
}

interface IProfileDropdownProps {
  menuItems: IProfileMenuItem[]
}

const ProfileDropdown = (props: IProfileDropdownProps) => {
  const { authStore } = useStores()
  const { userDetail, currentMembership, activeOrgId } = authStore
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isOpenAccountModal, setIsOpenAccountModal] = useState(false)
  const orgId = activeOrgId ?? 0
  const imageUrl = userDetail?.image
    ? getS3FileUrl(S3FileTypeEnum.IMAGE, orgId, userDetail.image)
    : ''
  // Use membership role if available, fallback to legacy authRoleId
  const role = getRoleNameFromId(currentMembership?.roleId) ?? getRole(userDetail?.authRoleId ?? 0)
  const name = getName(userDetail)

  useEffect(() => {
    const id = window.setTimeout(() => {
      const fromGoogleLink = sessionStorage.getItem(GOOGLE_LINK_OPEN_ACCOUNT_SESSION_KEY) === '1'
      const fromPasswordSetup = sessionStorage.getItem(OPEN_MY_ACCOUNT_PASSWORD_SETUP_SESSION_KEY) === '1'
      const urlParams = new URLSearchParams(window.location.search)
      const fromPasswordQuery = urlParams.get(OPEN_MY_ACCOUNT_URL_QUERY) === '1'
      if (!fromGoogleLink && !fromPasswordSetup && !fromPasswordQuery) {
        return
      }
      // Do not remove session keys here: React 18 Strict Mode remounts before paint and would
      // clear the flag before ProfileDropdown mounts again, so My Account never auto-opens.
      if (fromPasswordQuery) {
        urlParams.delete(OPEN_MY_ACCOUNT_URL_QUERY)
        const nextPath = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}${window.location.hash}`
        window.history.replaceState({}, '', nextPath)
      }
      setIsOpenAccountModal(true)
    }, 0)
    return () => window.clearTimeout(id)
  }, [])

  function toggleDropdown(): void {
    setIsDropdownOpen(!isDropdownOpen)
  }

  function toggleAccountModal(): void {
    if (isOpenAccountModal) {
      sessionStorage.removeItem(OPEN_MY_ACCOUNT_PASSWORD_SETUP_SESSION_KEY)
      sessionStorage.removeItem(GOOGLE_LINK_OPEN_ACCOUNT_SESSION_KEY)
    }
    setIsOpenAccountModal(!isOpenAccountModal)
  }

  function handleToggle(itemLabel: ProfileMenuLabels): void {
    if (itemLabel === ProfileMenuLabels.ACCOUNT) {
      toggleAccountModal()
    }
  }

  return (
    <>
      <Dropdown className={styles.dropdown} isOpen={isDropdownOpen} toggle={toggleDropdown}>
        <DropdownToggle
          data-toggle="dropdown"
          tag="button"
          className={cx('nav-link nav-user arrow-none mr-0', styles.dropdownToggle)}
          onClick={toggleDropdown}
          aria-expanded={isDropdownOpen}
        >
          <span className="account-user-avatar">
            <Avatar src={imageUrl} name={name} />
          </span>
          <span>
            <span className="account-user-name">{truncate(name, { length: 13 })}</span>
            <span className="account-position">{role}</span>
          </span>
        </DropdownToggle>
        <DropdownMenu right className="dropdown-menu-animated topbar-dropdown-menu profile-dropdown">
          <div onClick={toggleDropdown}>
            <div className="dropdown-header noti-title">
              <h6 className="text-overflow m-0">Welcome !</h6>
            </div>
            {props.menuItems.map((item, i) => {
              return (
                <Fragment key={i}>
                  {item?.redirectTo ? (
                    <Link to={item?.redirectTo} className="dropdown-item notify-item" key={i + '-profile-menu'}>
                      <i className={`${item.icon} mr-1`}></i>
                      <span>{item.label}</span>
                    </Link>
                  ) : (
                    <div
                      onClick={() => handleToggle(item.label)}
                      className={cx('dropdown-item notify-item', styles.pointer)}
                      key={i + '-profile-menu'}
                    >
                      <i className={`${item.icon} mr-1`}></i>
                      <span>{item.label}</span>
                    </div>
                  )}
                </Fragment>
              )
            })}
            <Link
              to={routes.logout.value}
              className="dropdown-item notify-item"
              key={'logout-profile-menu'}
              onClick={e => {
                e.preventDefault()
                authStore.logout()
              }}
            >
              <i className={`uil uil-exit mr-1`}></i>
              <span>Logout</span>
            </Link>
          </div>
        </DropdownMenu>
      </Dropdown>
      <EditAccountModal isProfile isOpen={isOpenAccountModal} toggle={toggleAccountModal} userId={userDetail?.id} />
    </>
  )
}

export default observer(ProfileDropdown)
